import React, { createContext, useContext, useState, useCallback } from 'react';

const ZakatContext = createContext(null);

// Nisab threshold - Gold Standard (87.48g gold)
const NISAB_GOLD_GRAMS = 87.48;
const ZAKAT_RATE = 0.025; // 2.5%

const initialPersonalAssets = {
  goldPrice: 7500, // Per gram in INR (user can adjust)
  silverPrice: 95, // Per gram in INR (user can adjust)
  gold24k: 0,
  gold22k: 0,
  gold18k: 0,
  silverJewellery: 0,
  silverCoins: 0,
  cashInHand: 0,
  bankSavings: 0,
  fixedDeposits: 0,
  recurringDeposits: 0,
  stocks: 0,
  mutualFunds: 0,
  bonds: 0,
  otherInvestments: 0,
  loansGiven: 0,
  personalDebts: 0,
};

const initialBusinessAssets = {
  // Retail
  retailInventory: 0,
  retailCashInHand: 0,
  retailBankBalance: 0,
  retailReceivables: 0,
  retailPayables: 0,
  // Hotel
  hotelCashInHand: 0,
  hotelBankBalance: 0,
  hotelReceivables: 0,
  hotelInventory: 0,
  hotelPayables: 0,
  // Real Estate
  realEstateRentalIncome: 0,
  realEstateCashFromSale: 0,
  realEstateAdvanceReceived: 0,
  realEstatePropertyForSale: 0,
  realEstateLiabilities: 0,
};

export const ZakatProvider = ({ children }) => {
  const [personalAssets, setPersonalAssets] = useState(initialPersonalAssets);
  const [businessAssets, setBusinessAssets] = useState(initialBusinessAssets);

  const updatePersonalAsset = useCallback((field, value) => {
    setPersonalAssets(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  const updateBusinessAsset = useCallback((field, value) => {
    setBusinessAssets(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  // Calculate Nisab threshold in INR
  const calculateNisab = useCallback(() => {
    return NISAB_GOLD_GRAMS * personalAssets.goldPrice;
  }, [personalAssets.goldPrice]);

  // Calculate personal assets value
  const calculatePersonalAssetsValue = useCallback(() => {
    const goldValue = 
      (personalAssets.gold24k * personalAssets.goldPrice) +
      (personalAssets.gold22k * personalAssets.goldPrice * 0.916) +
      (personalAssets.gold18k * personalAssets.goldPrice * 0.75);
    
    const silverValue = 
      (personalAssets.silverJewellery * personalAssets.silverPrice) +
      (personalAssets.silverCoins * personalAssets.silverPrice);
    
    const cashValue = 
      personalAssets.cashInHand +
      personalAssets.bankSavings +
      personalAssets.fixedDeposits +
      personalAssets.recurringDeposits;
    
    const investmentsValue = 
      personalAssets.stocks +
      personalAssets.mutualFunds +
      personalAssets.bonds +
      personalAssets.otherInvestments +
      personalAssets.loansGiven;
    
    const totalAssets = goldValue + silverValue + cashValue + investmentsValue;
    const totalLiabilities = personalAssets.personalDebts;
    
    return {
      goldValue,
      silverValue,
      cashValue,
      investmentsValue,
      totalAssets,
      totalLiabilities,
      netValue: totalAssets - totalLiabilities
    };
  }, [personalAssets]);

  // Calculate business assets value
  const calculateBusinessAssetsValue = useCallback(() => {
    // Retail
    const retailAssets = 
      businessAssets.retailInventory +
      businessAssets.retailCashInHand +
      businessAssets.retailBankBalance +
      businessAssets.retailReceivables;
    const retailLiabilities = businessAssets.retailPayables;
    const retailNet = retailAssets - retailLiabilities;

    // Hotel
    const hotelAssets = 
      businessAssets.hotelCashInHand +
      businessAssets.hotelBankBalance +
      businessAssets.hotelReceivables +
      businessAssets.hotelInventory;
    const hotelLiabilities = businessAssets.hotelPayables;
    const hotelNet = hotelAssets - hotelLiabilities;

    // Real Estate
    const realEstateAssets = 
      businessAssets.realEstateRentalIncome +
      businessAssets.realEstateCashFromSale +
      businessAssets.realEstateAdvanceReceived +
      businessAssets.realEstatePropertyForSale;
    const realEstateLiabilities = businessAssets.realEstateLiabilities;
    const realEstateNet = realEstateAssets - realEstateLiabilities;

    return {
      retail: { assets: retailAssets, liabilities: retailLiabilities, net: retailNet },
      hotel: { assets: hotelAssets, liabilities: hotelLiabilities, net: hotelNet },
      realEstate: { assets: realEstateAssets, liabilities: realEstateLiabilities, net: realEstateNet },
      totalAssets: retailAssets + hotelAssets + realEstateAssets,
      totalLiabilities: retailLiabilities + hotelLiabilities + realEstateLiabilities,
      netValue: retailNet + hotelNet + realEstateNet
    };
  }, [businessAssets]);

  // Calculate total zakat
  const calculateTotalZakat = useCallback(() => {
    const personal = calculatePersonalAssetsValue();
    const business = calculateBusinessAssetsValue();
    const nisab = calculateNisab();
    
    const totalNetWorth = personal.netValue + business.netValue;
    const isEligible = totalNetWorth >= nisab;
    const zakatAmount = isEligible ? totalNetWorth * ZAKAT_RATE : 0;

    return {
      personal,
      business,
      nisab,
      totalNetWorth,
      isEligible,
      zakatAmount,
      zakatRate: ZAKAT_RATE * 100
    };
  }, [calculatePersonalAssetsValue, calculateBusinessAssetsValue, calculateNisab]);

  const resetAll = useCallback(() => {
    setPersonalAssets(initialPersonalAssets);
    setBusinessAssets(initialBusinessAssets);
  }, []);

  const value = {
    personalAssets,
    businessAssets,
    updatePersonalAsset,
    updateBusinessAsset,
    calculateNisab,
    calculatePersonalAssetsValue,
    calculateBusinessAssetsValue,
    calculateTotalZakat,
    resetAll,
    NISAB_GOLD_GRAMS,
    ZAKAT_RATE
  };

  return (
    <ZakatContext.Provider value={value}>
      {children}
    </ZakatContext.Provider>
  );
};

export const useZakat = () => {
  const context = useContext(ZakatContext);
  if (!context) {
    throw new Error('useZakat must be used within a ZakatProvider');
  }
  return context;
};

export default ZakatContext;
